import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Search } from "lucide-react";
import { BrandIcon } from "@/components/app/Logo";
import { usePlatformBrand } from "@/components/app/PlatformBrandProvider";
import { ENVIRONMENT, API_BASE_URL } from "@/config.js";
import { getUser, getActiveOrgSlug } from "@/auth.js";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { SidebarContent } from "@/components/app/Sidebar";
import { ThemeToggle } from "@/components/app/ThemeToggle";
import { UserMenu } from "@/components/app/UserMenu";
import { ProgrammeSwitcher } from "@/components/app/ProgrammeSwitcher";

export function TopBar({ onSignedInOut }: { onSignedInOut?: boolean } = {}) {
  const signedIn = !!onSignedInOut;
  const brand = usePlatformBrand();
  const role = getUser()?.role;
  const { pathname } = useLocation();

  // The tenant's own branding logo, shown at the left of the header (opposite
  // the search box). Set under Organisation → Settings → Branding. Best-effort:
  // fetched once, and only rendered when the org actually has a logo.
  const [orgLogo, setOrgLogo] = useState<{ url: string; name: string } | null>(
    null,
  );
  useEffect(() => {
    if (!signedIn) return;
    const slug = getActiveOrgSlug();
    if (!slug) return;
    let cancelled = false;
    api
      .getOrgBranding()
      .then((b) => {
        if (cancelled || !b?.logo_r2_key) return;
        setOrgLogo({
          url: `${API_BASE_URL}/v1/public/brand-logo/${slug}?v=${encodeURIComponent(b.logo_r2_key)}`,
          name: b.organisation_name ?? "",
        });
      })
      .catch(() => {
        /* no org branding — leave the header logo unset */
      });
    return () => {
      cancelled = true;
    };
  }, [signedIn]);
  const showEnvPill = role === "super_admin" && ENVIRONMENT !== "production";
  const showPlatformBadge =
    role === "super_admin" && pathname.startsWith("/platform");
  return (
    <header
      role="banner"
      className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border/60 bg-glass px-4"
    >
      <div className="flex items-center gap-2">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open navigation">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <SheetHeader className="px-4 pt-4">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
            </SheetHeader>
            <SidebarContent />
          </SheetContent>
        </Sheet>
        <Link
          to="/"
          aria-label={`${brand.name} home`}
          className="flex items-center gap-2 lg:hidden"
        >
          <BrandIcon size="md" />
          <span className="text-sm font-semibold tracking-tight">{brand.name}</span>
        </Link>
        {/* Tenant branding logo — desktop only; on the LEFT, opposite the
            search box on the right. The sidebar already carries the mark on
            mobile, so this fills the empty desktop header space. */}
        {orgLogo && (
          <img
            src={orgLogo.url}
            alt={orgLogo.name || "Organisation logo"}
            className="hidden h-8 w-auto max-w-[180px] object-contain lg:block"
          />
        )}
      </div>
      <div className="flex items-center gap-2">
        {signedIn && (
          <button
            type="button"
            onClick={() =>
              window.dispatchEvent(new CustomEvent("exambase:open-search"))
            }
            className="hidden w-56 items-center gap-2 rounded-full bg-muted/40 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:flex md:w-72"
            aria-label="Search (⌘K)"
            title="Search (⌘K)"
          >
            <Search className="h-4 w-4 shrink-0" aria-hidden />
            <span className="flex-1 text-left">Search…</span>
            <kbd className="hidden rounded bg-background/80 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground md:inline">
              ⌘K
            </kbd>
          </button>
        )}
        {signedIn && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              window.dispatchEvent(new CustomEvent("exambase:open-search"))
            }
            className="sm:hidden"
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </Button>
        )}
        {showPlatformBadge && (
          <span
            className="hidden rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary sm:inline"
            title="Platform console (super admin)"
          >
            Platform admin
          </span>
        )}
        {showEnvPill && (
          <span
            className="rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning-text"
            title={`Environment: ${ENVIRONMENT}`}
          >
            {ENVIRONMENT}
          </span>
        )}
        {signedIn && <ProgrammeSwitcher />}
        <ThemeToggle />
        {signedIn && <UserMenu />}
      </div>
    </header>
  );
}
